import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, setClientToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const ClientLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/client/login`, formData);
      setClientToken(res.data.token);
      localStorage.setItem('clientName', res.data.name);
      localStorage.setItem('clientId', res.data.client_id);
      toast.success('Welcome back!');
      navigate('/portal/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="client-login-page" className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="/logo.png"
            alt="Eden's Dawn"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="font-heading text-3xl text-[#2C2C2C]">Client Portal</h1>
          <p className="text-[#6B7280] mt-2">Access your event details and timeline</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-soft p-8">
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Email</Label>
              <Input
                name="email"
                type="email"
                data-testid="client-email-input"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="input-elegant"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="client-password-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="input-elegant pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="client-login-btn"
              disabled={isLoading}
              className="w-full bg-[#4A5D23] hover:bg-[#6B7F3A] text-white rounded-full py-6"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </div>

          <div className="mt-6 p-4 bg-[#F5F5F0] rounded-lg text-sm text-[#6B7280]">
            <p className="font-medium mb-1">Demo Credentials:</p>
            <p>Email: client@demo.com</p>
            <p>Password: client123</p>
          </div>
        </form>

        <p className="text-center mt-6 text-sm text-[#6B7280]">
          Don't have an account? Contact us to get set up.
        </p>
      </div>
    </div>
  );
};

export default ClientLogin;
